import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "./config";
import { getCurrentUser, fetchAuthSession } from "@aws-amplify/auth";
import logo from "./KICKS-6.png";
import WhatsNewPopup from "./WhatsNewPopup";
import "./css/WelcomePage.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
const CHECK_COACH_API_URL =
  "https://lykns00a87.execute-api.us-east-2.amazonaws.com/default/football_app_data_crud";

const WelcomePage = ({ onSetupComplete, firstName, nameLoaded }) => {
  const navigate = useNavigate();
  const devLog = (...args) => {
    if (config.IS_DEV) {
      console.log(...args);
    }
  };
  const [userId, setUserId] = useState("");
  const [coachStatus, setCoachStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [gamesLeft, setGamesLeft] = useState(0);
  const [error, setError] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [freeTierUsed, setFreeTierUsed] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const promptDiscardMatch = () => setShowDiscardModal(true);
  const handleCancelDiscard = () => setShowDiscardModal(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [termsSigned, setTermsSigned] = useState(() => {
    const storedValue = localStorage.getItem("termsAccepted");
    return storedValue === "true"; // Initialize from localStorage
  });
  const [showWhatsNew, setShowWhatsNew] = useState(false);

  useEffect(() => {
    const checkTermsSigned = async () => {
      const termsAccepted = localStorage.getItem("termsAccepted");

      if (termsAccepted === "true") {
        devLog("Terms already accepted, skipping API call.");
        setTermsSigned(true);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.post(CHECK_COACH_API_URL, {
          operationType: "check_terms_and_policy_signed",
          data: { coach_id: userId },
        });

        devLog("Full API response:", response);

        // The Lambda now returns a simple boolean: true or false
        const isBothSigned = response.data;

        devLog("Lambda returned:", isBothSigned);

        if (isBothSigned) {
          devLog("Terms and privacy policy are signed.");
          setTermsSigned(true);
          localStorage.setItem("termsAccepted", "true");
        } else {
          devLog("Either terms or privacy policy are not signed.");
          setTermsSigned(false);
          localStorage.setItem("termsAccepted", "false");
        }
      } catch (error) {
        console.error("Error checking terms signed:", error);
        setTermsSigned(false);
        localStorage.setItem("termsAccepted", "false");
      } finally {
        setLoading(false);
      }
    };

    checkTermsSigned();
  }, [userId]);

  useEffect(() => {
    const checkWhatsNewStatus = async () => {
      if (!userId) {
        console.log("checkWhatsNewStatus: No userId yet, skipping check.");
        return;
      }

      console.log(
        "checkWhatsNewStatus: Checking What's New status for userId:",
        userId
      );

      try {
        const response = await axios.post(CHECK_COACH_API_URL, {
          operationType: "check_whats_new_status",
          data: { coach_id: userId },
        });

        console.log("checkWhatsNewStatus: Raw response received:", response);

        const data =
          typeof response.data.body === "string"
            ? JSON.parse(response.data.body)
            : response.data.body;

        console.log("checkWhatsNewStatus: Parsed data:", data);

        if (data === true || data?.show_whats_new === true) {
          console.log(
            "checkWhatsNewStatus: show_whats_new is TRUE, displaying popup."
          );
          setShowWhatsNew(true);
        } else {
          console.log(
            "checkWhatsNewStatus: show_whats_new is FALSE or undefined."
          );
        }
      } catch (error) {
        console.error(
          "checkWhatsNewStatus: Error checking What's New status:",
          error
        );
      }
    };

    checkWhatsNewStatus();
  }, [userId]);

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        const response = await axios.post(CHECK_COACH_API_URL, {
          operationType: "check_coach_subscription",
          data: { coach_id: userId },
        });

        const data =
          typeof response.data.body === "string"
            ? JSON.parse(response.data.body)
            : response.data.body;

        // Directly access the correct values
        setCurrentPlan(data.subscription_type || "unknown"); // Adjusted to match backend response
        setFreeTierUsed(data.free_tier_used || false); // Boolean already converted
        setGamesLeft(data.games_left_this_month || 0); // No need for parseInt as backend sends number
        setSubscriptionStatus(data.subscription_status || "unknown");
      } catch (err) {
        console.error("Error fetching subscription details:", err);
        setError("Failed to fetch subscription details.");
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionDetails();
  }, [userId]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/pricing-table.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const user = await getCurrentUser();
        devLog("Fetched user object:", user);

        if (user && (user.userId || user.username)) {
          const fetchedUserId = user.userId || user.username;
          devLog("Fetched userId (from user object):", fetchedUserId);
          setUserId(fetchedUserId);
        } else {
          console.error(
            "User does not have userId or username, attempting backup method."
          );
          const session = await fetchAuthSession();
          devLog("Fetched session object:", session);

          if (session && session.userSub) {
            const sessionUserId = session.userSub;
            devLog("Fetched userId (from session):", sessionUserId);
            setUserId(sessionUserId);
          } else {
            console.error(
              "Session does not have userSub or related properties."
            );
          }
        }
      } catch (error) {
        console.error("Error fetching userId:", error);
        setError("Failed to fetch user information");
      }
    };

    fetchUserId();
  }, []);

  // Previous useEffect for coach status remains the same...
  useEffect(() => {
    const checkCoachStatus = async () => {
      if (!userId) return;

      try {
        // Check the coach's status
        const statusResponse = await axios.post(CHECK_COACH_API_URL, {
          operationType: "check_coach_status",
          data: { coach_id: userId },
        });

        const statusBody =
          typeof statusResponse.data.body === "string"
            ? JSON.parse(statusResponse.data.body)
            : statusResponse.data.body;

        setCoachStatus(statusBody?.status || "Unknown");

        // If the coach exists but has no ongoing game, check games left
        if (statusBody?.status === "Coach exists with no ongoing game") {
          const gamesResponse = await axios.post(CHECK_COACH_API_URL, {
            operationType: "check_coach_details",
            data: { coach_id: userId },
          });

          const gamesBody =
            typeof gamesResponse.data.body === "string"
              ? JSON.parse(gamesResponse.data.body)
              : gamesResponse.data.body;

          setGamesLeft(gamesBody?.games_left_this_month || 0);
        }
      } catch (error) {
        console.error("Error checking coach status:", error);
        setError("Failed to check coach status");
      } finally {
        setLoading(false);
      }
    };

    checkCoachStatus();
  }, [userId]);

  const handleConfirmDiscardMatch = async () => {
    // Set loading state to show spinner on the button
    setIsDiscarding(true);
    try {
      // First API call: Clear current match
      const clearMatchResponse = await axios.post(CHECK_COACH_API_URL, {
        operationType: "clear_current_match",
        data: { coach_id: userId },
      });

      devLog("Clear match response:", clearMatchResponse);

      if (
        !(clearMatchResponse.status >= 200 && clearMatchResponse.status < 300)
      ) {
        console.error(
          "Failed to clear current match:",
          clearMatchResponse.statusText
        );
        setIsDiscarding(false);
        return;
      }

      // Second API call: Clear match_started flag
      const clearMatchStartedPayload = {
        operationType: "clear_match_started",
        data: { coach_id: userId },
      };

      devLog(
        "Payload for clearing match_started flag:",
        clearMatchStartedPayload
      );

      const clearMatchStartedResponse = await axios.post(
        CHECK_COACH_API_URL,
        clearMatchStartedPayload
      );

      devLog("Clear match_started response:", clearMatchStartedResponse);

      if (
        !(
          clearMatchStartedResponse.status >= 200 &&
          clearMatchStartedResponse.status < 300
        )
      ) {
        console.error(
          "Failed to clear match_started flag:",
          clearMatchStartedResponse.statusText
        );
        setIsDiscarding(false);
        return;
      }

      // Remove any local storage values if needed
      localStorage.removeItem("recordingsCount");

      // After a 1.5-second delay, perform a hard reload
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Error in discard match function:", error);
      setIsDiscarding(false);
    }
  };
  const fetchCurrentMatchData = async (coachId) => {
    const payload = {
      operationType: "fetch_current_match",
      data: { coach_id: coachId },
    };

    try {
      const response = await axios.post(CHECK_COACH_API_URL, payload);
      devLog("Raw match data response:", response.data);

      if (response.status !== 200) {
        throw new Error("Invalid response from server");
      }

      const matchDetails = response.data.body;

      const matchData = {
        matchFolderName: matchDetails.current_match.match_folder ?? null,
        myTeam: matchDetails.current_match.myTeam ?? null,
        opposition: matchDetails.current_match.opposition ?? null,
        matchId: matchDetails.current_match.matchId ?? null,
        matchDate: matchDetails.current_match.match_date ?? null,
        status: matchDetails.current_match.status ?? null,
        otherInfo: matchDetails.current_match.otherInfo ?? null,
        homeAway: matchDetails.current_match.homeAway ?? null,
        selectedTeamId: matchDetails.SK
          ? matchDetails.SK.replace(/^TEAM#/, "")
          : null,
        teamScores: {
          myTeamScore: matchDetails.current_match.teamScores.myTeamScore ?? 0,
          oppositionScore:
            matchDetails.current_match.teamScores.oppositionScore ?? 0,
        },
        players:
          matchDetails.current_match.playerSelections?.map((player) => ({
            player_id: player.player_id ?? null,
            player_name: player.player_name ?? null,
            goals: player.goals ?? 0,
            assists: player.assists ?? 0,
            shots_on_target: player.shots_on_target ?? 0,
            shots_off_target: player.shots_off_target ?? 0,
            defensive_actions: player.defensive_actions ?? 0,
            saves: player.saves ?? 0,
            position: player.position ?? "bench", // Add position here with a default value
          })) ?? [],
        // Add these additional fields
        formation: matchDetails.current_match.formation ?? null,
        formationCoordinates:
          matchDetails.current_match.formationCoordinates ?? [],
        formationPositions: Array.isArray(
          matchDetails.current_match.formationPositions
        )
          ? matchDetails.current_match.formationPositions
          : [],
        markerColor: matchDetails.current_match.markerColor ?? "#FF4C4C",
        gkColor: matchDetails.current_match.gkColor ?? "#4C6FFF",
        matchLength: matchDetails.current_match.matchLength ?? "10",
        numberOfParts: matchDetails.current_match.numberOfParts ?? "2",
        matchType: matchDetails.current_match.matchType ?? "5",
        selectedFormation: matchDetails.current_match.selectedFormation ?? null,
        positionAssignments:
          matchDetails.current_match.positionAssignments ?? [],
      };

      devLog("Parsed match data:", matchData);
      return matchData;
    } catch (error) {
      console.error("Error fetching current match data:", error);
      setError(`Failed to fetch match data: ${error.message}`);
      return null;
    }
  };

  const handleButtonClick = async () => {
    setError(null);
    setLoading(true);

    try {
      if (coachStatus === "Coach does not exist") {
        navigate("/SetupCoachPage", { state: { userId } });
      } else if (coachStatus === "Coach exists with an ongoing game") {
        devLog("Fetching current match data to continue.");
        const currentMatchData = await fetchCurrentMatchData(userId);

        if (currentMatchData) {
          const playerSelections = currentMatchData.players || [];

          // Navigate to PressToTalk with all the necessary match data.
          sessionStorage.setItem("accessedViaNavigation", "true");
          devLog("matchFolderName:", currentMatchData.matchFolderName);
          devLog("myTeam:", currentMatchData.myTeam);
          devLog("opposition:", currentMatchData.opposition);
          devLog("userId:", userId);
          devLog("selectedTeamId:", currentMatchData.selectedTeamId);
          devLog("matchId:", currentMatchData.matchId);
          devLog("matchFolder:", currentMatchData.matchFolderName);
          devLog("matchDate:", currentMatchData.matchDate);
          devLog("status:", currentMatchData.status);
          devLog("otherInfo:", currentMatchData.otherInfo);
          devLog("homeAway:", currentMatchData.homeAway);
          devLog("initialTeamScores:", currentMatchData.teamScores);
          devLog("playerSelections:", playerSelections);
          devLog("matchLength:", currentMatchData.matchLength);
          devLog("numberOfParts:", currentMatchData.numberOfParts);
          devLog("matchType:", currentMatchData.matchType);
          devLog("formation:", currentMatchData.formation);
          devLog("selectedFormation:", currentMatchData.selectedFormation);
          devLog("formationPositions:", currentMatchData.formationPositions);
          devLog(
            "formationCoordinates:",
            currentMatchData.formationCoordinates
          );
          devLog("markerColor:", currentMatchData.markerColor);
          devLog("gkColor:", currentMatchData.gkColor);
          devLog("positionAssignments:", currentMatchData.positionAssignments);
          navigate("/press-to-talk", {
            state: {
              matchFolderName: currentMatchData.matchFolderName,
              myTeam: currentMatchData.myTeam,
              opposition: currentMatchData.opposition,
              userId,
              selectedTeamId: currentMatchData.selectedTeamId,
              matchId: currentMatchData.matchId,
              matchFolder: currentMatchData.matchFolderName,
              matchDate: currentMatchData.matchDate,
              status: currentMatchData.status,
              otherInfo: currentMatchData.otherInfo,
              homeAway: currentMatchData.homeAway,
              initialTeamScores: currentMatchData.teamScores,
              playerSelections,
              matchLength: currentMatchData.matchLength,
              numberOfParts: currentMatchData.numberOfParts,
              matchType: currentMatchData.matchType,
              formation: currentMatchData.formation,
              selectedFormation: currentMatchData.selectedFormation,
              formationPositions: currentMatchData.formationPositions,
              formationCoordinates: currentMatchData.formationCoordinates,
              markerColor: currentMatchData.markerColor,
              gkColor: currentMatchData.gkColor,
              positionAssignments: currentMatchData.positionAssignments,
            },
          });
        } else {
          setError("Failed to fetch match data. Please try again.");
        }
      } else if (coachStatus === "Coach exists with no ongoing game") {
        if (gamesLeft > 0) {
          navigate("/match-setup");
        } else {
          setError(
            "You have no games left this month. Please upgrade your plan."
          );
        }
      }
    } catch (err) {
      console.error("Error in handleButtonClick:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-white-100">
      {showWhatsNew && (
        <WhatsNewPopup
          onClose={() => setShowWhatsNew(false)}
          onDontShowAgain={async () => {
            try {
              await axios.post(CHECK_COACH_API_URL, {
                operationType: "disable_whats_new",
                data: { coach_id: userId },
              });
            } catch (error) {
              console.error("Error disabling What's New popup:", error);
            }
          }}
        />
      )}
      <div>
        <img src={logo} alt="KICKS Logo" className="w-100 logo" />
      </div>
      {error && <p className="text-red-500 mt-4 mb-4">{error}</p>}
      {loading ? (
        <div>
          <div className="football-spinner"></div>
          <p>Loading...</p>
        </div>
      ) : (
        <>
          {coachStatus === "Coach does not exist" ? (
            // Skip terms for new coaches – navigate directly to setup
            <button
              onClick={() => navigate("/SetupCoachPage", { state: { userId } })}
              className="start-match-button"
            >
              Let's Start
            </button>
          ) : !termsSigned ? (
            // For existing coaches, check if they have accepted the terms
            <button
              onClick={() =>
                navigate("/terms-and-conditions", { state: { userId } })
              }
              className="start-match-button"
            >
              Review & Accept Terms
            </button>
          ) : (
            // If terms are accepted, show the normal button based on coach status
            <button
              onClick={handleButtonClick}
              className="start-match-button"
              disabled={
                coachStatus === "Coach exists with no ongoing game" &&
                gamesLeft === 0
              }
              title={
                gamesLeft === 0 ? "Upgrade your plan to unlock more games" : ""
              }
            >
              {coachStatus === "Coach exists with an ongoing game" ? (
                "Continue Match"
              ) : coachStatus === "Coach exists with no ongoing game" ? (
                <>
                  Match Setup
                  <span style={{ display: "block", fontSize: "0.9em" }}>
                    ({gamesLeft} games left)
                  </span>
                </>
              ) : (
                ""
              )}
            </button>
          )}
          {coachStatus === "Coach exists with an ongoing game" && (
            <button
              className="discard-match-modal-button"
              onClick={promptDiscardMatch}
            >
              <FontAwesomeIcon icon={faTrash} /> Discard Ongoing Match
            </button>
          )}
          <button
            onClick={() => navigate("/how-to-use")}
            className="how-to-use-button"
          >
            📖 App User Guide
          </button>
          {showDiscardModal && (
            <div
              className="discard-modal-overlay"
              onClick={handleCancelDiscard}
            >
              <div
                className="discard-modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <h2>Discard Ongoing Match</h2>
                <p>Are you sure you want to discard this match?</p>
                <div className="discard-modal-buttons">
                  <button
                    onClick={handleConfirmDiscardMatch}
                    className="discard-match-modal-button confirm"
                  >
                    {isDiscarding ? (
                      <div className="spinner" /> // Render your spinner component or element here
                    ) : (
                      "Yes, Discard"
                    )}
                  </button>

                  <button
                    onClick={handleCancelDiscard}
                    className="discard-modal-button-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Free plan message and upgrade button */}
          {currentPlan === "free" && (
            <>
              <p className="free-plan-message">*** Limited Trial Plan ***</p>
              <button
                onClick={() => navigate("/manage-subscription")}
                className="join-now-button"
                title="Upgrade to unlock more games"
              >
                Upgrade Your Plan
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default WelcomePage;
